import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useAlmaAvatar } from '../../../hooks/useAlmaAvatar';
import { useMessageReactions } from '../../../hooks/useMessageReactions';
import { useIsDarkTheme } from '../../../hooks/useTheme';
import { useConversationMessages } from '../../../hooks/useConversationMessages';
import { supabase } from '../../../lib/supabase';
import { markConversationSeen } from '../../../lib/conversationSeen';
import posthog from '../../../lib/posthog';
import {
  Avatar,
  ChatComposer,
  ChatHeader,
  ChatNotice,
  MessageBubble,
  Spinner,
  StatusBadge,
  SystemEvent,
} from '../../../components/design-system';
import { formatPlaces } from '../../../lib/places';
import type { Message, MatchStatus } from '../../../types';
import hourglassThemeDarkUrl from '../../../assets/animations/hourglass-theme-dark.webp?url';
import hourglassThemeLightUrl from '../../../assets/animations/hourglass-theme-light.webp?url';
import { scrollListToBottom } from '../../../lib/keyboard';

interface ChatMeta {
  conversationId: string;
  hostId: string;
  hostName: string | null;
  hostAvatar: string | null;
  hostCity: string | null;
  /** Cities THIS trip covers (from the portrait), not the host's primary city. */
  tripCities: string[];
  responseTime: string;
  status: MatchStatus;
  /** Last day of the trip window (start_date + trip_days − 1), if known. */
  tripEnd: Date | null;
  messages: Message[];
}

function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function TripChat() {
  const { id: matchId } = useParams<{ id: string }>();
  const { user, profile } = useAuth();
  // Theme is toggled from TripLayout's header, not here — useIsDarkTheme()
  // tracks the actual <html class="dark"> source of truth reactively.
  const isDarkTheme = useIsDarkTheme();
  const almaAvatar = useAlmaAvatar();
  const [meta, setMeta] = useState<ChatMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  // Bumped to re-run the full load — when the trip was handed to another host,
  // or when the app returns to the foreground (live updates pause meanwhile).
  const [reloadKey, setReloadKey] = useState(0);
  const metaRef = useRef<ChatMeta | null>(null);
  metaRef.current = meta;
  const { react } = useMessageReactions(meta?.conversationId, (updater) =>
    setMeta((prev) => (prev ? { ...prev, messages: updater(prev.messages) } : prev)),
  );

  useEffect(() => {
    if (!user || !matchId) return;
    void (async () => {
      // Find the match and host info
      const { data: match } = await supabase
        .from('matches')
        .select(`
          host_id,
          status,
          portrait_id,
          leg:trip_legs(city, start_date, days),
          host_profile:profiles!matches_host_id_fkey(full_name, avatar_url),
          conversation:conversations(id, messages)
        `)
        .eq('id', matchId)
        .eq('traveler_id', user.id)
        .maybeSingle();

      if (!match) { setLoading(false); return; }
      const raw = match as Record<string, unknown>;
      const hp = raw.host_profile as { full_name: string | null; avatar_url: string | null } | null;
      const convRows = raw.conversation as Array<{ id: string; messages: Message[] }> | null;
      const conv = convRows?.[0] ?? null;

      // A host of a trip with several cities covers one city: that city's last day
      // ends their part, and it is the only city to name in their chat.
      const legRaw = raw.leg as unknown;
      const leg = (Array.isArray(legRaw) ? legRaw[0] : legRaw) as { city: string; start_date: string; days: number } | null | undefined;

      // Trip window (for the "confirm finished" prompt once the trip is over).
      let tripEnd: Date | null = null;
      let tripCities: string[] = [];
      const portraitId = raw.portrait_id as string | null;
      if (portraitId) {
        const { data: pt } = await supabase
          .from('taste_portraits')
          .select('start_date, trip_days, destinations')
          .eq('id', portraitId)
          .maybeSingle();
        const p = pt as { start_date: string | null; trip_days: number | null; destinations: string[] | null } | null;
        tripCities = p?.destinations ?? [];
        if (p?.start_date && p.trip_days) {
          const end = new Date(p.start_date);
          end.setDate(end.getDate() + p.trip_days - 1);
          tripEnd = end;
        }
      }
      if (leg) {
        const end = new Date(leg.start_date);
        end.setDate(end.getDate() + leg.days - 1);
        tripEnd = end;
        tripCities = [leg.city];
      }

      const hostId = raw.host_id as string;
      let hostCity: string | null = null;
      let responseTime = '~1 day';
      if (hostId) {
        const { data: hd } = await supabase
          .from('host_profiles')
          .select('city, response_time')
          .eq('profile_id', hostId)
          .maybeSingle();
        if (hd) {
          hostCity = (hd as { city: string | null }).city;
          responseTime = (hd as { response_time?: string }).response_time ?? '~1 day';
        }
      }

      setMeta({
        conversationId: conv?.id ?? '',
        hostId,
        hostName: hp?.full_name ?? null,
        hostAvatar: hp?.avatar_url ?? null,
        hostCity,
        tripCities,
        responseTime,
        status: raw.status as MatchStatus,
        tripEnd,
        messages: conv?.messages ?? [],
      });
      setLoading(false);
    })();
  }, [user, matchId, reloadKey]);

  useEffect(() => {
    scrollListToBottom(bottomRef.current);
  }, [meta?.messages]);

  // Being on this page counts as having seen the conversation up to its
  // latest message — keeps the trip nav's unread dot in sync.
  useEffect(() => {
    if (meta?.conversationId) markConversationSeen(meta.conversationId);
  }, [meta?.conversationId, meta?.messages]);

  const refreshTrip = useCallback(async () => {
    if (!user || !matchId) return;
    const { data } = await supabase
      .from('matches')
      .select('status, host_id')
      .eq('id', matchId)
      .eq('traveler_id', user.id)
      .maybeSingle();
    if (!data) return;
    const { status, host_id } = data as { status: MatchStatus; host_id: string };
    if (metaRef.current && host_id !== metaRef.current.hostId) {
      // A new host means a new name, avatar and city — reload everything.
      setReloadKey((k) => k + 1);
      return;
    }
    setMeta((prev) => (prev && prev.status !== status ? { ...prev, status } : prev));
  }, [user, matchId]);

  // Real-time subscription. Besides new messages, every change to the trip
  // itself (host confirms, trip handed to another host) writes to this
  // conversation in the same transaction — so re-check the trip here, or the
  // page stays stuck on "reviewing your request".
  useConversationMessages(meta?.conversationId, (messages) => {
    setMeta((prev) => (prev ? { ...prev, messages } : prev));
    void refreshTrip();
  });

  // Live updates don't arrive while the app is backgrounded or the phone is
  // locked; catch up on anything missed when it comes back.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') setReloadKey((k) => k + 1); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  async function handleSend() {
    if (!input.trim() || !meta?.conversationId || !user) return;
    // Chat only opens once the host confirms — no messaging while pending.
    if (meta.status !== 'accepted') return;
    setSending(true);
    const content = input.trim();
    const newMsg: Message = {
      role: 'user',
      sender: user.id,
      content,
      timestamp: new Date().toISOString(),
    };
    setMeta((prev) => prev ? { ...prev, messages: [...prev.messages, newMsg] } : prev);
    setInput('');
    const { error: rpcError } = await supabase.rpc('append_chat_message', {
      p_conv_id: meta.conversationId,
      p_msg: newMsg,
    });
    if (rpcError) {
      console.error('append_chat_message failed:', rpcError);
    } else {
      posthog.capture('trip_message_sent');
    }

    // Push notification to host (fire-and-forget)
    if (meta.hostId) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
        fetch(`${supabaseUrl}/functions/v1/send-push`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({
            user_id: meta.hostId,
            title: profile?.full_name?.split(' ')[0] ?? 'Traveler',
            body: content.slice(0, 100),
            data: { type: 'chat', match_id: matchId },
          }),
        }).catch(() => {});
      }
    }
    setSending(false);
  }

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  if (!meta?.conversationId) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 text-center gap-3">
        <p className="text-sm text-app-muted">Chat isn't available yet — your host will be in touch once they confirm.</p>
      </div>
    );
  }

  const hostFirstName = meta.hostName?.split(' ')[0] ?? 'Host';
  const messages = meta.messages;
  // Until the host confirms, the trip chat is a read-only recap of the Alma
  // conversation — the host hasn't joined and messaging is closed.
  const awaitingHost = meta.status !== 'accepted';
  // The trip's own cities — a trip can span more than one, and the host's
  // primary city (the old source) misrepresented that.
  const placesLabel = formatPlaces(meta.tripCities) || meta.hostCity;
  // Completion (and the host's payout) is decided by the trip's own dates via
  // the stripe-complete-trips sweep, never by the traveler — this just
  // reflects that server-side state once it's flipped.
  const tripCompleted = meta.status === 'completed';

  return (
    <div className="flex-1 min-h-0 flex flex-col w-full max-w-[800px] mx-auto">
      {/* Chat header — soft gradient fade below */}
      <div className="relative z-[2] shrink-0">
        <ChatHeader
          name={meta.hostName ?? 'Your host'}
          subtitle={awaitingHost ? (placesLabel ?? undefined) : `Online · your host${placesLabel ? ` in ${placesLabel}` : ''}`}
          online={!awaitingHost}
          // While the host reviews, mirror the sidebar's gold "Pending" pill
          // rather than inventing a second visual language for the same state.
          actions={awaitingHost ? <StatusBadge status="pending" label="Reviewing your trip" variant="status" /> : undefined}
          avatar={<Avatar name={meta.hostName ?? 'Host'} src={meta.hostAvatar ?? undefined} size={44} />}
          style={{ padding: '16px 24px' }}
        />
        <div
          className="absolute left-0 right-0 top-full h-10 pointer-events-none z-[1]"
          style={{ background: 'linear-gradient(to bottom, var(--bg), transparent)' }}
        />
      </div>

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-6 pb-4 flex flex-col gap-4" data-keyboard-pin>
        {messages.length === 0 && (
          <div className="flex justify-center py-8">
            <p className="text-xs text-app-muted">Your conversation will appear here.</p>
          </div>
        )}
        {messages.map((msg, i) => {
          if (msg.role === 'system') return <SystemEvent key={i}>{msg.content}</SystemEvent>;

          // Messages drafted in the anonymous /try chat carry no `sender` —
          // there's no account yet when they're written — so fall back to
          // `role`. Without this the traveler's own history renders as Alma's.
          const isUser = msg.role === 'user' && (!msg.sender || msg.sender === user?.id);
          const isAlma = msg.role === 'assistant' || msg.sender === 'alma';

          // Within a run from the same sender, only the newest bubble shows avatar + name.
          const next = messages[i + 1];
          const lastInGroup = !next || next.role === 'system' || next.sender !== msg.sender;
          const senderLabel = !isUser && lastInGroup ? (isAlma ? 'Alma' : hostFirstName) : undefined;

          return (
            <MessageBubble
              key={i}
              from={isUser ? 'you' : 'other'}
              time={formatTime(msg.timestamp)}
              senderName={senderLabel}
              avatar={!isUser && lastInGroup ? (
                isAlma
                  ? <Avatar kind="alma" name="Alma" src={almaAvatar ?? undefined} size={28} />
                  : <Avatar name={meta.hostName ?? 'Host'} src={meta.hostAvatar ?? undefined} size={28} />
              ) : undefined}
              reserveGutter={!isUser && !lastInGroup}
              reaction={msg.reaction}
              onReact={(emoji) => void react(i, emoji)}
            >
              {msg.content}
            </MessageBubble>
          );
        })}
        {awaitingHost && (
          <ChatNotice
            figure={
              // Animated WebP, not a <video>: iOS draws a play button over a video
              // it won't autoplay (Low Power Mode); an image always plays.
              <img
                key={isDarkTheme ? 'dark' : 'light'}
                alt=""
                aria-hidden
                src={isDarkTheme ? hourglassThemeDarkUrl : hourglassThemeLightUrl}
                style={{ width: 140, height: 106, objectFit: 'contain' }}
              />
            }
            headline={`${hostFirstName} is reviewing your request.`}
            headlineSize="sm"
            headlineWrap
            messageVariant="caption"
            message="They'll confirm before anything is final. If they can't take it, we'll find you another host and let you know here."
          />
        )}
        <div ref={bottomRef} className="h-px shrink-0" />
      </div>

      {/* Trip completion is decided by the trip's dates, not the traveler — this
          only reflects it once the sweep has already released the payout. */}
      {tripCompleted && (
        <div className="shrink-0 px-5">
          <div className="rounded-xl border border-app-border bg-app-card px-4 py-3 flex items-center gap-2.5">
            <i className="ti ti-circle-check text-[--success-fg] text-base" />
            <p className="text-xs text-app-fg-2">
              Trip complete — {hostFirstName}'s payout is on its way. Thanks for travelling with Sekretly.
            </p>
          </div>
        </div>
      )}

      {/* Composer + disclaimer — tight fixed gap below on mobile, where the
          floating tab bar that follows already owns the safe-area clearance;
          desktop has no tab bar underneath, so it keeps the original padding. */}
      <div className="shrink-0 px-5 pb-2.5 md:pb-5 md:pb-safe-or-5 relative">
        <div
          className="absolute left-0 right-0 bottom-full h-10 pointer-events-none z-[1]"
          style={{ background: 'linear-gradient(to bottom, transparent, var(--bg))' }}
        />
        <ChatComposer
          value={input}
          onChange={setInput}
          onSend={() => void handleSend()}
          placeholder={
            awaitingHost
              ? `You can message ${hostFirstName} once they confirm`
              : `Message ${hostFirstName}…`
          }
          disabled={sending || awaitingHost}
          disclaimer="Never share passport numbers, banking details, or home address in chat."
        />
      </div>
    </div>
  );
}

export default TripChat;
